import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Container } from "react-bootstrap";
import { useNavigationType, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import RequirementListSection from "./RequirementListSection";
import RequirementModal from "./RequirementModal";
import ViewRequirementModal from "./ViewRequirementModal";
import RequirementFilterModal from "./RequirementFilterModal";
import {
    fetchMarketplaceRequirements,
    deleteMarketplaceRequirement,
    updateMarketplaceRequirement,
    fetchMarketplaceCategories,
    fetchMarketplaceRequirementStats
} from "../../../redux/slice/marketplaceSlice";
import { fetchActiveCities, fetchActiveStates } from "../../../redux/slice/locationSlice";

import {
    PublishedIcon,
    TotalItemsIcon,
    MarketPlaceIcon
} from "../../../assets/icons";
import CommonButton from "../../../components/commonButton";
import { useToast } from "../../../context/TostContext";
import CommonDeletePopup from "../../../components/delete-popup";
import { usePermission } from "../../../hooks/usePermission";
import { TabCard } from "../../../components/custom-card/Cards";
import { FilterBtn } from "../../../components/commonButton/btns";
import { TableSearch } from "../../../components/form/Fields";

const MODULE_SLUG = "marketplace_listings";

export default function RequirementList() {
    const navigationType = useNavigationType();
    const dispatch = useDispatch();
    const toast = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    const requirementId = searchParams.get("requirementId");


    const headerPortalTarget = document.getElementById("header-portal-target");

    const headerContent = (
        <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="fw-semibold mb-0 text-primary fs-lg">
                <span className="text-muted fw-normal">Marketplace / </span>Requirements
            </h4>
        </div>
    );

    const [limit, setLimit] = useState(() => {
        if (navigationType === "POP") {
            const savedLimit = sessionStorage.getItem("req_limit");
            if (savedLimit) return JSON.parse(savedLimit);
        }
        return { value: 10, label: 10 };
    });

    const [currentPage, setCurrentPage] = useState(() => {
        if (navigationType === "POP") {
            const savedPage = sessionStorage.getItem("req_page");
            if (savedPage) return parseInt(savedPage, 10);
        }
        return 1;
    });

    const [cardStatus, setCardStatus] = useState(() => {
        if (navigationType === "POP") {
            return sessionStorage.getItem("req_cardStatus") || "";
        }
        return "";
    });

    const [searchTerm, setSearchTerm] = useState(() => {
        return navigationType === "POP" ? sessionStorage.getItem("req_searchTerm") || "" : "";
    });

    const [filterModal, setFilterModal] = useState(false);
    const [filterValues, setFilterValues] = useState(() => {
        if (navigationType === "POP") {
            const saved = sessionStorage.getItem("req_filters");
            if (saved) {
                try {
                    const parsed = JSON.parse(saved);
                    return {
                        category_id: Array.isArray(parsed.category_id) ? parsed.category_id : [],
                        state_id: Array.isArray(parsed.state_id) ? parsed.state_id : [],
                        city_id: Array.isArray(parsed.city_id) ? parsed.city_id : []
                    };
                } catch (e) {
                    // ignore and use defaults
                }
            }
        }
        return {
            category_id: [],
            state_id: [],
            city_id: []
        };
    });

    const [reqModal, setReqModal] = useState({ show: false, editData: null, isView: false });
    const [deleteModal, setDeleteModal] = useState({ show: false, itemId: null });

    const { hasPermission } = usePermission();
    const canCreate = hasPermission(MODULE_SLUG, "create");
    const canUpdate = hasPermission(MODULE_SLUG, "update");
    const canDelete = hasPermission(MODULE_SLUG, "delete");
    const canRead = hasPermission(MODULE_SLUG, "read");

    const { requirements, requirementPagination, requirementStats, categories, isLoading, isInitialLoad } = useSelector((state) => state.marketplace);

    const RequirementStatsCards = [
        { id: 1, total: requirementStats?.total || 0, title: "All", icon: <TotalItemsIcon />, status: "", iconClassName: "icon-secondary" },
        { id: 2, total: requirementStats?.open || 0, title: "Open", icon: <PublishedIcon />, status: "open", iconClassName: "icon-info" },
        { id: 3, total: requirementStats?.closed || 0, title: "Closed", icon: <PublishedIcon />, status: "closed", iconClassName: "icon-secondary" }
    ];

    const loadStats = useCallback(() => {
        dispatch(fetchMarketplaceRequirementStats());
    }, [dispatch]);

    const handleCardClick = (value) => {
        setCardStatus(value);
        setCurrentPage(1);
    };

    const isInitialFetchRef = useRef(true);
    const searchTimeout = useRef(null);

    const getRequirements = useCallback((isDebounced = false) => {
        const params = {
            page: currentPage,
            limit: limit?.value,
            search: searchTerm,
            status: cardStatus,
            category_ids: Array.isArray(filterValues.category_id) && filterValues.category_id.length > 0 ? filterValues.category_id.join(',') : undefined,
            state_ids: Array.isArray(filterValues.state_id) && filterValues.state_id.length > 0 ? filterValues.state_id.join(',') : undefined,
            city_ids: Array.isArray(filterValues.city_id) && filterValues.city_id.length > 0 ? filterValues.city_id.join(',') : undefined
        };

        const fetchList = () => {
            dispatch(fetchMarketplaceRequirements(params));
        };

        if (isDebounced) {
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
            searchTimeout.current = setTimeout(fetchList, 500);
        } else {
            fetchList();
        }

        sessionStorage.setItem("req_limit", JSON.stringify(limit));
        sessionStorage.setItem("req_page", currentPage.toString());
        sessionStorage.setItem("req_cardStatus", cardStatus);
        sessionStorage.setItem("req_searchTerm", searchTerm);
        sessionStorage.setItem("req_filters", JSON.stringify(filterValues));

    }, [dispatch, currentPage, limit, searchTerm, cardStatus, filterValues]);

    useEffect(() => {
        getRequirements(isInitialFetchRef.current ? false : true);
        isInitialFetchRef.current = false;
    }, [getRequirements]);

    useEffect(() => {
        loadStats();
        dispatch(fetchMarketplaceCategories());
    }, [dispatch, loadStats]);

    useEffect(() => {
        if (requirementId && requirements?.length > 0) {
            const req = requirements.find(item => String(item.id) === String(requirementId));
            if (req) {
                setReqModal({ show: true, editData: req, isView: true });
            }
        } else {
            setReqModal(prev => prev.isView ? { show: false, editData: null, isView: false } : prev);
        }
    }, [requirementId, requirements]);

    const fetchStatesOptions = useCallback((search, page) => {
        return dispatch(fetchActiveStates({ search, page, limit: 20 })).unwrap();
    }, [dispatch]);

    const fetchCitiesOptions = useCallback((search, page) => {
        return dispatch(fetchActiveCities({
            search,
            page,
            limit: 20,
            state_ids: Array.isArray(filterValues.state_id) && filterValues.state_id.length > 0 ? filterValues.state_id.join(',') : undefined
        })).unwrap();
    }, [dispatch, filterValues.state_id]);

    const handleApplyFilters = () => {
        setCurrentPage(1);
        setFilterModal(false);
    };

    const handleClearFilters = () => {
        setFilterValues({
            category_id: [],
            state_id: [],
            city_id: []
        });
        setCurrentPage(1);
        setFilterModal(false);
    };

    const handleView = (item) => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.set("requirementId", item.id);
            return params;
        });
    };

    const handleEdit = (item) => {
        setReqModal({ show: true, editData: item, isView: false });
    };

    const handleStatusToggle = async (item) => {
        try {
            const nextStatus = item.status === "open" ? "closed" : "open";
            await dispatch(updateMarketplaceRequirement({
                id: item.id,
                data: { status: nextStatus }
            })).unwrap();
            toast.success(`Requirement status updated to ${nextStatus}`);
            loadStats();
        } catch (error) {
            toast.error(error || "Failed to update status");
        }
    };

    const handleDeleteClick = (item) => {
        setDeleteModal({ show: true, itemId: item.id });
    };

    const handleDeleteConfirm = async () => {
        try {
            await dispatch(deleteMarketplaceRequirement(deleteModal.itemId)).unwrap();
            toast.success("Requirement deleted successfully");
            setDeleteModal({ show: false, itemId: null });
            getRequirements(false);
            loadStats();
        } catch (error) {
            toast.error(error || "Delete failed");
        }
    };

    const actionList = [
        {
            title: "View",
            handler: (row) => handleView(row),
            isVisible: () => canRead
        },
        {
            title: "Edit",
            handler: (row) => handleEdit(row),
            isVisible: () => canUpdate
        },
        {
            title: (row) => row.status === "open" ? "Mark Closed" : "Mark Open",
            handler: (row) => handleStatusToggle(row),
            isVisible: () => canUpdate
        },
        {
            title: "Delete",
            handler: (row) => handleDeleteClick(row),
            isVisible: () => canDelete,
            className: "text-danger"
        }
    ];

    return (
        <>
            {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
            <Container fluid className="px-0">
                {/* Statistics Cards */}
                <div className="grid gap-4">
                    {RequirementStatsCards.map((card) => (
                        <TabCard
                            key={card.id}
                            title={card.title}
                            count={card.total}
                            icon={card.icon}
                            onClick={() => handleCardClick(card.status)}
                            active={cardStatus === card.status}
                            iconClassName={card.iconClassName}
                            className="g-col-4 g-col-xxl-4"
                        />
                    ))}
                </div>

                {/* Filtering & Search bar */}
                <div className="bg-white rounded-2 common-shadow mt-4">
                    <div className="p-4 d-flex align-items-center justify-content-between border-bottom flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-3">

                            <TableSearch placeholder="Search items..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }} />
                            <FilterBtn onClick={() => setFilterModal(true)} />

                        </div>
                        {canCreate && (
                            <CommonButton
                                title="Add Requirement"
                                icon={<MarketPlaceIcon size={16} />}
                                variant="primary"
                                onClick={() => setReqModal({ show: true, editData: null, isView: false })}
                            />
                        )}
                    </div>

                    <RequirementListSection
                        data={requirements}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        pageSize={limit.value}
                        setPageSize={(val) => {
                            setLimit({ value: val, label: val });
                            setCurrentPage(1);
                        }}
                        totalItems={requirementPagination?.total}
                        isLoading={isLoading}
                        isInitialLoad={isInitialLoad}
                        handleView={handleView}
                        actionList={actionList}
                    />
                </div>

                {/* Advanced Filters Modal */}
                <RequirementFilterModal
                    isOpen={filterModal}
                    onClose={() => setFilterModal(false)}
                    title="Advanced Filters"
                    size="lg"
                    categories={categories}
                    filterValues={filterValues}
                    setFilterValues={setFilterValues}
                    fetchStatesOptions={fetchStatesOptions}
                    fetchCitiesOptions={fetchCitiesOptions}
                    onApply={handleApplyFilters}
                    onClear={handleClearFilters}
                />

                <RequirementModal
                    isOpen={reqModal.show && !reqModal.isView}
                    onClose={() => setReqModal({ show: false, editData: null, isView: false })}
                    editData={reqModal.editData}
                    isView={reqModal.isView}
                    onSuccess={() => {
                        getRequirements(false);
                        loadStats();
                    }}
                    title={reqModal.editData ? "Edit Requirement" : "Add Requirement"}
                    size="lg"
                    className="common-modal"
                />

                <ViewRequirementModal
                    isOpen={reqModal.show && reqModal.isView}
                    onClose={() => {
                        setReqModal({ show: false, editData: null, isView: false });
                        setSearchParams((prev) => {
                            const params = new URLSearchParams(prev);
                            params.delete("requirementId");
                            return params;
                        });
                    }}
                    data={reqModal.editData}
                    size="lg"
                />

                {/* Delete Confirmation Popup */}
                <CommonDeletePopup
                    show={deleteModal.show}
                    onHide={() => setDeleteModal({ show: false, itemId: null })}
                    onClickSave={handleDeleteConfirm}
                    title="Delete Requirement"
                    content="Are you sure you want to delete this requirement? This action cannot be undone."
                    saveTitle="Delete"
                />
            </Container>
        </>
    );
}
